import React, { useState, useRef } from 'react'
import { Menu } from '@headlessui/react'
import { PrinterIcon, EnvelopeIcon } from '@heroicons/react/20/solid'
import { useTranslation } from 'react-i18next'
import { SEND_TICKET_TO_EMAIL } from 'graphql/booking/mutations'
import { useMutation } from '@apollo/client'
import { ErrorDialog, SuccessDialog } from './ResponseDialog'
import { useReactToPrint } from 'react-to-print'
import { BoatBookingImage } from 'components/icons'
import { customTimeFormat } from 'utils/common'
import { QRCodeCanvas } from 'qrcode.react'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'

export default function TicketActions({ bookingData }) {
    const componentRef = useRef(null)
    const { t } = useTranslation()
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [dialogMessage, setDialogMessage] = useState('')

    const printFn = useReactToPrint({
        contentRef: componentRef,
        documentTitle: 'AwesomeFileName',
    })

    const [sendBookingTicket, { loading: sendLoading }] = useMutation(SEND_TICKET_TO_EMAIL, {
        onCompleted: data => {
            if (data?.sendBookingTicket.code === '200' && data?.sendBookingTicket.success === true) {
                setShowSucDialog(true)
                setDialogMessage(data?.sendBookingTicket.message)
                setTimeout(() => {
                    setShowSucDialog(false)
                }, 3000)
            } else {
                setDialogMessage(data?.sendBookingTicket.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setDialogMessage(t('Something went wrong, please try again later'))
            setShowErrDialog(true)
        },
    })

    const emailValidationSchema = Yup.object().shape({
        email: Yup.string().email(t('Invalid email format')).required(t('Email is required')),
    })

    const handleSendEmail = (values, { setSubmitting, resetForm }) => {
        // Get travel_date from the first trip segment if available, otherwise from metadata
        const travelDate = bookingData?.trip_segments?.[0]?.travel_date || bookingData?.metadata?.travel_date

        // Validate that travel_date is available
        if (!travelDate) {
            setDialogMessage(t('Travel date is not available for this booking'))
            setShowErrDialog(true)
            setSubmitting(false)
            return
        }

        sendBookingTicket({
            variables: {
                dispatch: {
                    booking_id: bookingData?.booking_id,
                    travel_date: travelDate,
                    channel: 'EMAIL',
                    email: values.email || null,
                    phone_number: null,
                },
            },
        }).then(() => {
            setSubmitting(false)
            resetForm()
        })
    }

    return (
        <>
            <div className="flex gap-x-2">
                <button
                    type="button"
                    onClick={printFn}
                    disabled={
                        !['CNF', 'ONF', 'CMP', 'NOS', 'ONP', 'PRC', 'TRC', 'PND'].includes(bookingData?.status?.code)
                    }
                    className={`flex items-center justify-center rounded-md px-3 py-1.5 text-sm 
        font-semibold leading-6 text-white shadow-sm focus-visible:outline 
        focus-visible:outline-2 focus-visible:outline-offset-2 ${
            ['CNF', 'ONF', 'CMP', 'NOS', 'ONP', 'PRC', 'TRC', 'PND'].includes(bookingData?.status?.code)
                ? 'hover:bg-primary/[.9] bg-primary focus-visible:outline-primary'
                : 'bg-gray-400 cursor-not-allowed'
        }
    `}
                >
                    <PrinterIcon aria-hidden="true" className="h-5 w-5 pe-0.5 me-2" />
                    {t('Print Ticket')}
                </button>

                <Menu as="div" className="relative print:hidden">
                    {({ close }) => (
                        <>
                            <Menu.Button
                                disabled={bookingData?.status?.code !== 'CNF'}
                                className={`flex items-center justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${
                                    bookingData?.status?.code === 'CNF'
                                        ? 'hover:bg-primary/[.9] bg-primary focus-visible:outline-primary'
                                        : 'bg-gray-400 cursor-not-allowed'
                                }`}
                            >
                                <EnvelopeIcon aria-hidden="true" className="h-5 w-5 pe-0.5 me-2" />
                                {t('Email Ticket')}
                            </Menu.Button>
                            <Menu.Items className="absolute right-0 mt-2 w-80 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                                <div className="p-4">
                                    <Formik
                                        initialValues={{ email: '' }}
                                        validationSchema={emailValidationSchema}
                                        onSubmit={(values, formikHelpers) => {
                                            handleSendEmail(values, formikHelpers)
                                            close()
                                        }}
                                    >
                                        {({ errors, touched, isSubmitting }) => (
                                            <Form>
                                                <div className="mb-4">
                                                    <label
                                                        htmlFor="email"
                                                        className="block text-sm font-medium text-gray-700"
                                                    >
                                                        {t('Email Address')}
                                                    </label>
                                                    <Field
                                                        type="email"
                                                        id="email"
                                                        name="email"
                                                        className={`mt-1 block w-full rounded-md border ${
                                                            errors.email && touched.email
                                                                ? 'border-red-500'
                                                                : 'border-gray-300'
                                                        } px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-primary`}
                                                        placeholder={t('Enter email address')}
                                                    />
                                                    {errors.email && touched.email && (
                                                        <div className="text-red/100 text-sm mt-1">{errors.email}</div>
                                                    )}
                                                </div>
                                                <button
                                                    type="submit"
                                                    disabled={isSubmitting || sendLoading}
                                                    className="w-full flex items-center justify-center hover:bg-primary/[.9] bg-primary focus-visible:outline-primary rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                                                >
                                                    {isSubmitting || sendLoading ? t('Sending...') : t('Send')}
                                                </button>
                                            </Form>
                                        )}
                                    </Formik>
                                </div>
                            </Menu.Items>
                        </>
                    )}
                </Menu>
            </div>

            {/* Hidden printable content */}
            <div ref={componentRef} className="hidden print:block print:p-0">
                {bookingData?.trip_segments && (
                    <div className="print:p-4">
                        {bookingData.trip_segments.map((segment, segmentIndex) =>
                            segment.passengers?.map((info, index) => (
                                <div
                                    key={`${segment.segment_id}-${index}`}
                                    id="tickets_card"
                                    className={`rounded-3xl bg-white p-0 shadow-xl ring-1 ring-gray-900/10 sm:p-10 print:h-auto print:shadow-none print:ring-0 print:rounded-none ${
                                        index === 0 && segmentIndex === 0 ? 'mt-0' : 'mt-6'
                                    } ${segmentIndex > 0 && index === 0 ? 'print:break-before-page' : ''}`}
                                >
                                    <div className="relative flex flex-col w-full">
                                        {/* Trip Type Indicator */}
                                        {bookingData.trip_segments.length > 1 && (
                                            <div className="text-center mb-4">
                                                <span className="inline-block bg-primary text-white px-4 py-2 rounded-full text-sm font-semibold">
                                                    {segmentIndex === 0 ? t('Departure Trip') : t('Return Trip')}
                                                </span>
                                            </div>
                                        )}

                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center my-1">
                                                <h2 className="capitalize">
                                                    <div className="flex-auto text-xs text-gray-400 my-1">
                                                        <span>Passenger name</span>
                                                    </div>
                                                    <span className="font-bold text-[20px]">
                                                        {info.passenger.given_name} {info.passenger.family_name}{' '}
                                                    </span>
                                                </h2>
                                            </div>
                                            <div className="ml-auto text-blue-800">
                                                <div className="flex-auto text-xs text-gray-400 my-1">
                                                    <span>Ticket No</span>
                                                </div>
                                                {info.ticket_number}
                                            </div>
                                        </div>
                                        <div className="relative border-dashed border-b-2 my-5"></div>
                                        <div className="flex items-center">
                                            <div className="flex flex-col">
                                                <div className="flex-auto text-xs text-gray-400 my-1">
                                                    <span>From Station</span>
                                                </div>
                                                <div className="w-full flex-none text-lg text-blue-800 font-bold leading-none">
                                                    {segment?.start?.location?.name?.en || 'N/A'}
                                                </div>

                                                <div className="text-sm mt-1 whitespace-nowrap">
                                                    <span className="text-gray-600">Departure :</span>{' '}
                                                    {customTimeFormat(segment?.start?.departure_time)}
                                                </div>
                                            </div>
                                            <div className="flex flex-col mx-auto">
                                                <BoatBookingImage className="w-[50px] h-[50px] fill-[#285f74] mt-3 rounded-full bg-primary" />
                                            </div>
                                            <div className="flex flex-col">
                                                <div className="flex-auto text-xs text-gray-400 my-1">
                                                    <span>To Station</span>
                                                </div>
                                                <div className="w-full flex-none text-lg text-blue-800 font-bold leading-none">
                                                    {segment?.end?.location?.name?.en || 'N/A'}
                                                </div>
                                                <div className="text-sm mt-1">
                                                    <span className="text-gray-600">Arrival :</span>{' '}
                                                    {customTimeFormat(segment?.end?.arrival_time)}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="relative border-dashed border-b-2 my-3 pt-2">
                                            <div className="absolute rounded-full w-5 h-5 bg-blue-900 -mt-2 -left-2"></div>
                                            <div className="absolute rounded-full w-5 h-5 bg-blue-900 -mt-2 -right-2"></div>
                                        </div>
                                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-12 gap-y-8 items-center p-4 pb-0 px-0 text-sm">
                                            <div className="flex flex-col">
                                                <span className="text-sm text-gray-600">Boat Name</span>
                                                <div className="font-semibold">{segment?.route?.boat?.name?.en}</div>
                                            </div>
                                            <div className="flex flex-col">
                                                <span className="text-sm text-gray-600 whitespace-nowrap">
                                                    Boat Number
                                                </span>
                                                <div className="font-semibold">
                                                    {segment?.route?.boat?.details?.reg_number}
                                                </div>
                                            </div>
                                            <div className="flex flex-col text-sm">
                                                <span className="text-gray-600 whitespace-nowrap">Seat Number</span>
                                                <div className="font-semibold">{info?.seat_info.seat_no}</div>
                                            </div>
                                            <div className="flex flex-col text-sm">
                                                <span className="text-gray-600">Seat Type</span>
                                                <div className="font-semibold">{info?.seat_info.seat_type_code}</div>
                                            </div>
                                        </div>

                                        <div className="border-dashed border-b-2 my-3 pt-5">
                                            <div className="absolute rounded-full w-5 h-5 bg-blue-900 -mt-2 -left-2"></div>
                                            <div className="absolute rounded-full w-5 h-5 bg-blue-900 -mt-2 -right-2"></div>
                                        </div>
                                        <div className="flex flex-col py-5 pb-0 justify-center text-sm mt-1">
                                            <h6 className="font-bold text-center">
                                                Present this QR Code while onboarding
                                            </h6>
                                            <div id="qrcode">
                                                <QRCodeCanvas
                                                    value={info.qr_code?.encrypted}
                                                    className="m-auto mt-3 mb-6 !h-[250px] !w-[250px]"
                                                />
                                            </div>
                                            <div className="flex justify-center w-full flex-col text-center">
                                                <p className="flex-auto text-xs text-gray-400 my-1">
                                                    Booking Reference
                                                </p>
                                                <p className="font-bold">{bookingData?.booking_code}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>

            {showSucDialog && (
                <SuccessDialog
                    status={true}
                    title={t('Success')}
                    message={dialogMessage}
                    onClose={() => setShowSucDialog(false)}
                />
            )}
            {showErrDialog && (
                <ErrorDialog
                    status={true}
                    title={t('Error')}
                    message={dialogMessage}
                    onClose={() => setShowErrDialog(false)}
                />
            )}
        </>
    )
}
